import { MouseEvent, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import useAlertStore from '@/stores/alertStore';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useOrganizations, useOrganizationInvites } from '../hooks/useOrganizations';
import type { OrganizationInvite } from '../types';

interface PendingInvitesSectionProps {
  organizationId: string;
}

const formatRelative = (iso: string) => {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const PendingInvitesSection = ({ organizationId }: PendingInvitesSectionProps) => {
  const showAlert = useAlertStore((state) => state.showAlert);
  const { revokeInvite, inviteMember } = useOrganizations();
  const { data, isLoading, error, refetch } = useOrganizationInvites(organizationId);
  const [busyTokens, setBusyTokens] = useState<Set<string>>(new Set());
  const [menu, setMenu] = useState<{ anchor: HTMLElement; token: string } | null>(null);

  const markBusy = (token: string) =>
    setBusyTokens((prev) => {
      const next = new Set(prev);
      next.add(token);
      return next;
    });

  const clearBusy = (token: string) =>
    setBusyTokens((prev) => {
      const next = new Set(prev);
      next.delete(token);
      return next;
    });

  if (isLoading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (error) {
    return null;
  }

  const pending: OrganizationInvite[] = (data?.invites ?? []).filter((invite) => invite.status === 'pending');

  if (pending.length === 0) {
    return null;
  }

  const openMenu = (event: MouseEvent<HTMLElement>, token: string) => {
    setMenu({ anchor: event.currentTarget, token });
  };

  const closeMenu = () => setMenu(null);

  const activeInvite = menu ? (pending.find((invite) => invite.token === menu.token) ?? null) : null;

  const handleRevoke = async (invite: OrganizationInvite) => {
    closeMenu();
    markBusy(invite.token);
    try {
      await revokeInvite({ token: invite.token, organizationId });
      showAlert(`Invite to ${invite.email} canceled.`, 'success');
    } catch (error: unknown) {
      showAlert(getApiErrorMessage(error, 'Could not cancel invite. Please try again.'), 'error');
    } finally {
      clearBusy(invite.token);
    }
  };

  const handleResend = async (invite: OrganizationInvite) => {
    closeMenu();
    markBusy(invite.token);
    // Revoke-then-invite: track which step failed. If the revoke succeeded but
    // the re-invite failed, the original invite is gone — "try again" would be
    // misleading, so tell the owner to send a fresh invite instead.
    let revoked = false;
    try {
      await revokeInvite({ token: invite.token, organizationId });
      revoked = true;
      await inviteMember({ organizationId, email: invite.email });
      showAlert(`Invite resent to ${invite.email}.`, 'success');
    } catch (error) {
      if (revoked) {
        const detail = getApiErrorMessage(error, '');
        showAlert(
          `The previous invite was canceled but the new one failed to send${detail ? ` (${detail})` : ''}. ` +
            `Please send a fresh invite to ${invite.email}.`,
          'error',
        );
      } else {
        showAlert(getApiErrorMessage(error, 'Could not resend invite. Please try again.'), 'error');
      }
      // Refetch so the pending list reflects reality (the revoke may have landed).
      refetch();
    } finally {
      clearBusy(invite.token);
    }
  };

  return (
    <>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
        {pending.map((invite) => {
          const isBusy = busyTokens.has(invite.token);
          return (
            <Stack
              key={invite.token}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 1.5, py: 1.25, minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'grey.200',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <MailOutlineIcon fontSize="small" />
              </Box>
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                  {invite.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Invited {formatRelative(invite.created_at)}
                </Typography>
              </Stack>
              <Box sx={{ minWidth: 80, display: 'flex', justifyContent: 'flex-end' }}>
                <Chip label="Pending" size="small" color="warning" variant="outlined" />
              </Box>
              {isBusy ? (
                <Box sx={{ width: 34, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                </Box>
              ) : (
                <IconButton
                  aria-label={`Invite actions for ${invite.email}`}
                  size="small"
                  onClick={(event) => openMenu(event, invite.token)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          );
        })}
      </Stack>
      <Menu anchorEl={menu?.anchor ?? null} open={!!menu} onClose={closeMenu}>
        <MenuItem onClick={() => activeInvite && handleResend(activeInvite)}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Resend invite" secondary="Personal note won't be included" />
        </MenuItem>
        <MenuItem onClick={() => activeInvite && handleRevoke(activeInvite)}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Cancel invite" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default PendingInvitesSection;
